import { StormBuildUpRowDef } from './stormBuildUpTypes';

// Per-section configurable field, mirrors stormBuildUpPonds.ts's
// PondConfigurableField/POND_CONFIGURABLE_FIELDS registry.
export interface SwaleConfigurableField {
  suffix: string; // row id suffix, e.g. `swale_2_${suffix}`
  label: string;
  describe: (mm: number) => string;
}

export const SWALE_CONFIGURABLE_FIELDS: SwaleConfigurableField[] = [
  { suffix: 'topsoilSiteWon', label: 'Top soil (site won), xxxmm thick', describe: (mm) => `Spread and level ${mm}mm thick top soil - site won` },
  { suffix: 'topsoilImported', label: 'Top soil (imported), xxxmm thick', describe: (mm) => `Spread and level ${mm}mm thick top soil - imported` },
];

// No default value: matches posRowDefs.ts's/stormBuildUpPonds.ts's convention
// — an unconfigured row keeps showing this static "xxx" placeholder text,
// only switching to the real mm value once that section's Configure modal
// sets one > 0.
const SWALE_TOPSOIL_SITEWON_PLACEHOLDER = 'Spread and level xxxmm thick top soil - site won';
const SWALE_TOPSOIL_IMPORTED_PLACEHOLDER = 'Spread and level xxxmm thick top soil - imported';

// exTopsoilMm defaults to Global Settings' "Ex Topsoil Depth Utilised"
// fallback (0.3m × 1000), same convention as stormBuildUpPonds.ts's makePondRows.
export const makeSwaleRows = (n: number, exTopsoilMm: number = 300, topsoilSiteWonMm?: number, topsoilImportedMm?: number, sectionTitle?: string): StormBuildUpRowDef[] => [
  { id: `swale_${n}_hdr`, description: `SWALE ${n}${sectionTitle?.trim() ? ` ${sectionTitle.trim().toUpperCase()}` : ''}`, unit: '', defaultQuant: null, titleSuffix: sectionTitle?.trim() ? sectionTitle.trim().toUpperCase() : undefined, isHeader: true },
  { id: `swale_${n}_strip`, description: 'Site strip to remove vegetation', unit: 'm2', defaultQuant: null },
  { id: `swale_${n}_excTop`, description: `Excavate to reduce levels to take up ${exTopsoilMm}mm top soil, deposit onsite`, unit: 'm3', defaultQuant: null },
  { id: `swale_${n}_excLvl`, description: 'Excavate to reduce levels', unit: 'm3', defaultQuant: null },
  { id: `swale_${n}_fill`, description: 'Filling with site won subsoil', unit: 'm3', defaultQuant: null },
  { id: `swale_${n}_cart`, description: 'Load, cart and bund excavated material', unit: 'm3', defaultQuant: null },
  { id: `swale_${n}_level`, description: 'Level and compact formation, batter embankments', unit: 'm2', defaultQuant: null },
  { id: `swale_${n}_topsoilSiteWon`, description: topsoilSiteWonMm ? SWALE_CONFIGURABLE_FIELDS[0].describe(topsoilSiteWonMm) : SWALE_TOPSOIL_SITEWON_PLACEHOLDER, unit: 'm2', defaultQuant: null },
  { id: `swale_${n}_topsoilImported`, description: topsoilImportedMm ? SWALE_CONFIGURABLE_FIELDS[1].describe(topsoilImportedMm) : SWALE_TOPSOIL_IMPORTED_PLACEHOLDER, unit: 'm2', defaultQuant: null },
];

export const DEFAULT_SWALE_SECTIONS = 1;
export const MAX_SWALE_SECTIONS = 15;

// rowId is the full `swale_<n>_<suffix>` id — parses out the suffix and
// looks up its registry entry, if any (mirrors getPondConfigurableField).
export const getSwaleConfigurableField = (rowId: string): SwaleConfigurableField | undefined => {
  const match = rowId.match(/^swale_\d+_(.+)$/);
  if (!match) return undefined;
  return SWALE_CONFIGURABLE_FIELDS.find((f) => f.suffix === match[1]);
};

export const getSwalesRows = (
  sectionCount: number = MAX_SWALE_SECTIONS,
  exTopsoilMm?: number,
  swaleTopsoilThicknessMm: Record<string, number> = {},
  sectionTitles: Record<string, string> = {}
): StormBuildUpRowDef[] =>
  Array.from({ length: sectionCount }, (_, i) => {
    const n = i + 1;
    const topsoilSiteWonMm = swaleTopsoilThicknessMm[`swale_${n}_topsoilSiteWon`];
    const topsoilImportedMm = swaleTopsoilThicknessMm[`swale_${n}_topsoilImported`];
    return makeSwaleRows(n, exTopsoilMm, topsoilSiteWonMm, topsoilImportedMm, sectionTitles[`swale_${n}_hdr`]);
  }).flat();

// Full fixed set — used by Spoil Reconciliation / Excel import-export, which
// need every SWALE 1-N row id available regardless of how many sections the
// build-up UI currently shows.
export const SWALES_ROWS: StormBuildUpRowDef[] = getSwalesRows(MAX_SWALE_SECTIONS);

// Shifts a row-id-keyed map's `swale_<n>_*` keys down by one wherever n >
// deletedN, and drops the deleted section's own keys — mirrors
// shiftPondSectionKeys/shiftPosSectionKeys. Keys with other prefixes
// (pond_/hw_/ps_, which share the same pondsSwales.table) pass through untouched.
export const shiftSwaleSectionKeys = <T,>(map: Record<string, T>, deletedN: number): Record<string, T> => {
  const next: Record<string, T> = {};
  Object.entries(map).forEach(([id, value]) => {
    const match = id.match(/^swale_(\d+)_(.+)$/);
    if (!match) {
      next[id] = value;
      return;
    }
    const idx = Number(match[1]);
    if (idx === deletedN) return;
    const newIdx = idx > deletedN ? idx - 1 : idx;
    next[`swale_${newIdx}_${match[2]}`] = value;
  });
  return next;
};
